import type { GetInsetAreaProcedure } from "@logic/lib/2d"
import { nameShape, getInsetAreas } from "@logic/lib/2d"
import type { InstallArea } from "@logic/lib/design"
import { studioToScreen } from "@logic/lib/design"
import { matchAdjacentPairs } from "@logic/lib/matching"
import type { Face2d, Face2dComputed } from "@logic/lib/mesh"
import type { GetRecoilValue, RecoilState } from "recoil"
import { atom, selectorFamily } from "recoil"

import { SLOPE_VALUES } from "~/containers/Proposal/state/designs/slope-values"
import { molecule } from "~/utils/recoil"

import { getComputedEdges } from "./edges"
import { findNodeState, projectNode } from "./nodes"
import type { TransactionOperation } from "./transactions"
import { readonlyTransactors } from "./transactions"
import { zoomState } from "../view"

export const DEFAULT_AREA: InstallArea = {
  nodeIds: [],
  mountLocation: `roof`,
  azimuth: null,
  azimuthLocked: false,
  slope: SLOPE_VALUES.AVERAGE,
  material: `shingles`,
  manualShading: 1,
  sunroofShading: null,
  preferSunroof: false,
}

export const [findAreaState, areaIndex, allAreasState] = molecule<
  InstallArea,
  string
>({
  key: `area`,
  default: { ...DEFAULT_AREA },
})

export const areaSelectionState = atom<Set<string>>({
  key: `areaSelection`,
  default: new Set(),
})

export const computeFace = <FACE extends Face2d>(
  get: GetRecoilValue,
  faceId: string,
  findFaceState: (faceId: string) => RecoilState<FACE>,
): FACE & Face2dComputed => {
  const area = get(findFaceState(faceId))

  return {
    ...area,
    nodes: area.nodeIds.map((id) => get(findNodeState(id))),
  }
}

export const projectFace = <FACE extends Face2d>(
  get: GetRecoilValue,
  faceId: string,
  findFaceState: (faceId: string) => RecoilState<FACE>,
): FACE & Face2dComputed & { id: string } => {
  const area = get(findFaceState(faceId))

  return {
    ...area,
    id: faceId,
    nodes: area.nodeIds.map((id) => projectNode(get, id)),
  }
}

export const findComputedAreaState = selectorFamily<
  Face2dComputed & InstallArea,
  string
>({
  key: `computedArea`,
  get:
    (areaId) =>
    ({ get }) =>
      computeFace(get, areaId, findAreaState),
})

export const findProjectedAreaState = selectorFamily<
  Face2dComputed & InstallArea,
  string
>({
  key: `projectedArea`,
  get:
    (areaId) =>
    ({ get }) =>
      projectFace(get, areaId, findAreaState),
})

export const getInsetAreaFaces: TransactionOperation<
  string,
  { result: Face2dComputed[]; procedure: GetInsetAreaProcedure }
> = (transactors, areaId) => {
  const { get } = transactors
  const area = computeFace(get, areaId, findAreaState)
  const edges = matchAdjacentPairs(area.nodeIds, (nodeId, nextNodeId) =>
    getComputedEdges(transactors, nameShape(nodeId, nextNodeId)),
  )

  return getInsetAreas({ areaId, area, edges })
}

export const getProjectedInsetAreaFaces: TransactionOperation<
  string,
  { result: Face2dComputed[]; procedure: GetInsetAreaProcedure }
> = (transactors, areaId) => {
  const { get } = transactors
  const { result, procedure } = getInsetAreaFaces(transactors, areaId)
  const zoom = get(zoomState)

  return {
    result: result.map((area) => ({
      ...area,
      nodes: area.nodes.map((node) => studioToScreen.point(node, zoom)),
    })),
    procedure,
  }
}

export const findInsetAreaFacesState = selectorFamily<
  {
    result: Face2dComputed[]
    procedure: GetInsetAreaProcedure
  },
  string
>({
  key: `insetAreaFaces`,
  get:
    (areaId) =>
    ({ get }) => {
      try {
        return getProjectedInsetAreaFaces(readonlyTransactors(get), areaId)
      } catch (thrown) {
        console.warn(`install area broke`, thrown)

        return {
          result: [],
          procedure: {
            insetArea: DEFAULT_AREA,
            dividedAreas: [],
            meshIntersections: [],
            dividedMeshNodeGroups: [],
            nodeEdgePairsThatAreTooClose: [],
            nodesNotInOriginalArea: [],
            nodesEliminated: [],

            linksBetweenInsets: [],
            insetsAndLinks: [],
            intersections: [],
          },
        }
      }
    },
})
